import type {
  ConnectorHealth,
  CustomerJourney,
  DemandDriver,
  EmergingSignal,
  InteractionInsight,
  Intervention,
  MaterialChange,
  RootCauseHypothesis,
  TaxonomyVersion,
  UUIDv7,
} from "./domain";

const id = (n: number): UUIDv7 =>
  `019b9a3e-7f42-7${String(n % 1000).padStart(3, "0")}-8a2c-${String(n).padStart(12, "0")}`;
const confidence = (value: number, reason: string, evidenceCoverage = 92) => ({
  value,
  reason,
  evidenceCoverage,
});
export const organisationId = id(1);

const interactionTemplates = [
  {
    summary:
      "Document capture closed after three attempts; the customer switched from app to phone support.",
    intent: "Complete identity verification",
    theme: ["Account access", "Identity verification", "Document capture"] as [
      string,
      string,
      string,
    ],
    driver: "Identity verification failure",
    product: "Mobile App",
    process: "Identity verification",
    journey: "Open a current account",
    channel: "Phone" as const,
    outcome: "Escalated",
    signals: ["3 failed attempts", "channel switch", "repeated explanation"],
    mode: "supporting" as const,
  },
  {
    summary:
      "The collection date in the confirmation email differs from the portal by four days.",
    intent: "Confirm payment date",
    theme: [
      "Payments & billing",
      "Direct debit",
      "Collection date mismatch",
    ] as [string, string, string],
    driver: "Payment date confusion",
    product: "Current Account",
    process: "Plan change billing",
    journey: "Change account plan",
    channel: "Email" as const,
    outcome: "Resolved",
    signals: ["repeat contact", "policy clarification"],
    mode: "supporting" as const,
  },
  {
    summary:
      "Document capture succeeded after the customer enabled camera permission manually.",
    intent: "Complete identity verification",
    theme: [
      "Account access",
      "Identity verification",
      "Device permissions",
    ] as [string, string, string],
    driver: "Identity verification failure",
    product: "Mobile App",
    process: "Identity verification",
    journey: "Open a current account",
    channel: "Chat" as const,
    outcome: "Resolved",
    signals: ["manual workaround"],
    mode: "contradictory" as const,
  },
  {
    summary:
      "Delivery status was clear in the revised tracking page; no assisted contact followed.",
    intent: "Track delivery",
    theme: ["Orders & delivery", "Delivery tracking", "Status clarity"] as [
      string,
      string,
      string,
    ],
    driver: "Order status",
    product: "Delivery",
    process: "Order tracking",
    journey: "Receive a card",
    channel: "App" as const,
    outcome: "Resolved",
    signals: ["self-service completed"],
    mode: "supporting" as const,
  },
  {
    summary: "The source record is restricted to the Vulnerable Customer team.",
    intent: "Discuss support needs",
    theme: ["Account support", "Accessibility", "Additional support"] as [
      string,
      string,
      string,
    ],
    driver: "Necessary service",
    product: "Current Account",
    process: "Customer support",
    journey: "Manage account",
    channel: "Case" as const,
    outcome: "Restricted",
    signals: ["permission boundary"],
    mode: "insufficient" as const,
  },
  {
    summary:
      "The renewal letter gives the total price but not the reason for the increase.",
    intent: "Understand renewal price",
    theme: [
      "Renewal & retention",
      "Price explanation",
      "Renewal breakdown",
    ] as [string, string, string],
    driver: "Renewal price explanation",
    product: "Insurance",
    process: "Policy renewal",
    journey: "Renew insurance",
    channel: "Phone" as const,
    outcome: "Resolved",
    signals: ["long handling time", "repeated explanation"],
    mode: "supporting" as const,
  },
] as const;

const interactionContexts = [
  "The customer had already attempted the relevant self-service journey.",
  "The interaction began within 24 hours of the linked operational change.",
  "The source record shows an unsuccessful digital step before assisted contact.",
  "A second contact followed within two days of the original journey.",
  "The source metadata links the interaction to the affected process and cohort.",
  "The recorded outcome agrees with the surrounding case history.",
  "The interaction sits within the highest-deviation customer cohort.",
  "The record contains a complete source journey and confirmed operational outcome.",
] as const;

export const interactionInsights: InteractionInsight[] = Array.from(
  { length: 48 },
  (_, index) => {
    const item = interactionTemplates[index % interactionTemplates.length]!;
    const summary = `${item.summary} ${interactionContexts[Math.floor(index / interactionTemplates.length) % interactionContexts.length]}`;
    const permissionState =
      index % 17 === 4 ? "denied" : index % 13 === 4 ? "redacted" : "granted";
    return {
      id: id(100 + index),
      organisationId,
      sourceSystem:
        item.channel === "Phone"
          ? "Genesys Cloud"
          : item.channel === "App"
            ? "Northstar App"
            : "Dynamics 365",
      sourceReference: `NS-${String(48190 + index)}`,
      channel: item.channel,
      occurredAt: `2026-07-${String(18 - (index % 12)).padStart(2, "0")}T${String(8 + (index % 10)).padStart(2, "0")}:20:00Z`,
      customerReference: `CUST-${String(8100 + index)}`,
      caseReference: `CAS-${String(24000 + index)}`,
      accountReference: `ACC-${String(6100 + (index % 20))}`,
      intent: item.intent,
      themePath: item.theme,
      contactDriver: item.driver,
      effort: {
        inferred: 3 + (index % 7),
        reportedCes: index % 4 === 0 ? 2 + (index % 4) : undefined,
        signals: [...item.signals],
      },
      sentimentProgression:
        index % 3 === 0 ? ["neutral", "frustrated"] : ["concerned", "relieved"],
      outcome: item.outcome,
      repeatJourneyId: index % 3 === 0 ? id(500 + (index % 8)) : undefined,
      product: item.product,
      process: item.process,
      journey: item.journey,
      summary,
      importantMoments: [
        summary.split(";")[0] ?? summary,
        `Outcome recorded as ${item.outcome.toLowerCase()}.`,
      ],
      factState: index % 5 === 0 ? "fact" : "inference",
      confidence: confidence(
        82 + (index % 15),
        `${6 + (index % 8)} corroborating records and consistent source metadata`,
        86 + (index % 13),
      ),
      modelVersion: "vox-intelligence-2.4",
      instructionVersion: "ops-2026.07.3",
      taxonomyVersion: "v12.4",
      processedAt: "2026-07-18T10:24:00Z",
      permissionState,
      permissionReason:
        permissionState === "denied"
          ? "Your role does not include Vulnerable Customer case evidence."
          : permissionState === "redacted"
            ? "Personal identifiers are hidden by Northstar policy."
            : undefined,
      evidenceMode: item.mode,
    };
  },
);

export const rootCauseHypotheses: RootCauseHypothesis[] = [
  {
    id: id(301),
    title: "Android document-capture regression",
    symptom: "Identity-verification contacts increased 38%",
    cause: "Version 8.14 camera lifecycle regression",
    contributingFactors: [
      "Android 14 camera permissions",
      "Fallback guidance is not shown",
      "31% release adoption at onset",
    ],
    supportingEvidenceIds: interactionInsights
      .filter((x) => x.contactDriver.includes("Identity"))
      .filter((x) => x.evidenceMode === "supporting")
      .slice(0, 8)
      .map((x) => x.id),
    contradictoryEvidenceIds: interactionInsights
      .filter((x) => x.evidenceMode === "contradictory")
      .slice(0, 3)
      .map((x) => x.id),
    affectedCohorts: ["Android 14", "New customers", "Digital only"],
    explanatoryContribution: 31,
    confidence: confidence(
      88,
      "Timing, device concentration and failure language align; iOS remains in range",
      91,
    ),
    operationalEvents: [
      { at: "2 Jul 09:00", label: "Android 8.14 reached 25%", kind: "release" },
      {
        at: "2 Jul 09:42",
        label: "Failure language exceeded range",
        kind: "signal",
      },
    ],
    suggestedInvestigation:
      "Reproduce camera lifecycle failure on Android 14 and compare SDK telemetry with 8.13.",
    proposedOwner: "Mobile Platform",
    outcome: "Application abandoned or moved to assisted service",
  },
  {
    id: id(302),
    title: "Direct-debit date presentation mismatch",
    symptom: "Payment-date contacts increased 24%",
    cause: "Portal and confirmation service use different effective-date rules",
    contributingFactors: [
      "Mid-cycle plan changes",
      "Four-day display difference",
      "No explanatory copy",
    ],
    supportingEvidenceIds: interactionInsights
      .filter((x) => x.contactDriver.includes("Payment"))
      .slice(0, 9)
      .map((x) => x.id),
    contradictoryEvidenceIds: interactionInsights
      .filter((x) => x.contactDriver.includes("Payment"))
      .slice(-2)
      .map((x) => x.id),
    affectedCohorts: ["Plan changers", "Monthly direct debit", "Assisted"],
    explanatoryContribution: 24,
    confidence: confidence(
      91,
      "Date pairs match in 89% of sampled cases; two records indicate agent miscoding",
      94,
    ),
    operationalEvents: [
      { at: "8 Jul", label: "Billing rules deployed", kind: "process" },
      { at: "9 Jul", label: "Mismatch language accelerated", kind: "signal" },
    ],
    suggestedInvestigation:
      "Compare effective-date derivation in portal and notification service for mid-cycle changes.",
    proposedOwner: "Billing Operations",
    outcome: "Repeat contact and payment reassurance",
  },
  {
    id: id(303),
    title: "Renewal letter lacks price decomposition",
    symptom: "Renewal explanation contacts increased 17%",
    cause: "Letter template shows totals without component change",
    contributingFactors: ["Policy fee change", "Dense regulatory copy"],
    supportingEvidenceIds: interactionInsights
      .filter((x) => x.contactDriver.includes("Renewal"))
      .slice(0, 7)
      .map((x) => x.id),
    contradictoryEvidenceIds: [],
    affectedCohorts: ["Insurance renewal", "Over 65", "Phone"],
    explanatoryContribution: 13,
    confidence: confidence(
      84,
      "Representative language is consistent, but campaign mix may also contribute",
      83,
    ),
    operationalEvents: [
      { at: "10 Jul", label: "Renewal batch issued", kind: "campaign" },
    ],
    suggestedInvestigation:
      "Test a component-level price breakdown against current letter.",
    proposedOwner: "Retention Communications",
    outcome: "Longer handling time",
  },
  {
    id: id(304),
    title: "Delivery-status language created reassurance demand",
    symptom: "Order-status contact remained above its expected range",
    cause: "Tracking page did not explain carrier scan states",
    contributingFactors: [
      "Ambiguous in-transit status",
      "No next-update expectation",
      "Customers switched to assisted service for reassurance",
    ],
    supportingEvidenceIds: interactionInsights
      .filter((x) => x.contactDriver === "Order status")
      .slice(0, 7)
      .map((x) => x.id),
    contradictoryEvidenceIds: [],
    affectedCohorts: ["Delivery customers", "Digital only"],
    explanatoryContribution: 13,
    confidence: confidence(
      86,
      "Contact declined after clearer language launched and stayed lower in two regions",
      89,
    ),
    operationalEvents: [
      {
        at: "8 Jul",
        label: "Revised tracking language launched",
        kind: "intervention",
      },
      { at: "15 Jul", label: "Contact moved below baseline", kind: "signal" },
    ],
    suggestedInvestigation:
      "Complete the observation window and verify that failed-delivery complaints remain within guardrail.",
    proposedOwner: "Digital Fulfilment",
    outcome: "Reassurance calls and channel switching",
  },
];

export const materialChanges: MaterialChange[] = [
  {
    id: id(201),
    title: "Assisted contact volume increased",
    metric: "Interactions",
    baseline: 50238,
    currentValue: 56480,
    absoluteChange: 6242,
    relativeChange: 12.4,
    expectedRange: [49200, 52300],
    contributors: [
      {
        id: "android",
        label: "Android identity verification",
        value: 1842,
        hypothesisId: id(301),
      },
      {
        id: "debit",
        label: "Direct-debit date confusion",
        value: 918,
        hypothesisId: id(302),
      },
      {
        id: "renewal",
        label: "Renewal explanation",
        value: 860,
        hypothesisId: id(303),
      },
    ],
    offsets: [{ id: "delivery", label: "Delivery tracking", value: -612 }],
    explainedShare: 68,
    residual: 3234,
    affectedCohorts: ["Android 14", "Plan changers", "Insurance renewal"],
    firstSeen: "2 Jul, 09:42",
    evidenceCount: 2445,
    confidence: confidence(
      91,
      "Four independently observed contributors explain 68% of movement",
      96,
    ),
    impact:
      "6,242 additional interactions require capacity and product decisions",
  },
  {
    id: id(202),
    title: "Identity verification abandonment increased",
    metric: "Abandonment rate",
    baseline: 11.2,
    currentValue: 16.7,
    absoluteChange: 5.5,
    relativeChange: 49.1,
    expectedRange: [10.1, 12.4],
    contributors: [
      {
        id: "android",
        label: "Android document capture",
        value: 4.1,
        hypothesisId: id(301),
      },
    ],
    offsets: [],
    explainedShare: 75,
    residual: 1.4,
    affectedCohorts: ["Android 14", "New customers"],
    firstSeen: "2 Jul, 09:42",
    evidenceCount: 614,
    confidence: confidence(
      88,
      "Release timing and device concentration align; causality is not confirmed",
      91,
    ),
    impact: "1,100 fewer weekly onboarding completions",
  },
  {
    id: id(203),
    title: "Delivery reassurance demand fell",
    metric: "Order-status contacts",
    baseline: 5655,
    currentValue: 4920,
    absoluteChange: -735,
    relativeChange: -13,
    expectedRange: [5350, 5880],
    contributors: [
      {
        id: "tracking",
        label: "Revised tracking language",
        value: -612,
        hypothesisId: id(303),
      },
    ],
    offsets: [],
    explainedShare: 83,
    residual: -123,
    affectedCohorts: ["Delivery customers", "Digital only"],
    firstSeen: "8 Jul, 16:20",
    evidenceCount: 1284,
    confidence: confidence(
      86,
      "Decline follows phased rollout and is consistent in two regions",
      89,
    ),
    impact: "Fewer channel switches and reassurance calls",
    positive: true,
  },
  {
    id: id(204),
    title: "Renewal handling time increased",
    metric: "Average handling minutes",
    baseline: 8.1,
    currentValue: 12.4,
    absoluteChange: 4.3,
    relativeChange: 53.1,
    expectedRange: [7.8, 8.7],
    contributors: [
      {
        id: "renewal",
        label: "Price explanation",
        value: 3.2,
        hypothesisId: id(303),
      },
    ],
    offsets: [],
    explainedShare: 74,
    residual: 1.1,
    affectedCohorts: ["Insurance renewal", "Phone"],
    firstSeen: "10 Jul, 09:30",
    evidenceCount: 742,
    confidence: confidence(
      84,
      "Long-call language strongly aligns; campaign mix remains an alternative",
      83,
    ),
    impact: "Higher service effort and queue pressure",
  },
];

export const emergingSignals: EmergingSignal[] = materialChanges.map(
  (change, index) => ({
    id: id(401 + index),
    title: change.title,
    observation:
      index === 2
        ? "Order-status contacts are below the expected range after revised tracking language launched."
        : `${change.metric} moved outside its expected range.`,
    severity:
      index === 0
        ? "Critical"
        : index === 2
          ? "Positive"
          : index === 3
            ? "Moderate"
            : "High",
    acceleration: index === 2 ? -13 : ([38, 24, 17][index] ?? 17),
    firstSeen: change.firstSeen,
    trajectory: index === 2 ? "declining" : "accelerating",
    affectedPopulation: change.affectedCohorts.join(" · "),
    likelyCauses: change.contributors.map((c) => c.label),
    alternatives:
      index === 0
        ? [
            "Campaign mix increased new applicants",
            "Android 14 permission changes",
          ]
        : ["Seasonality", "Source coding change"],
    supportingEvidenceIds: interactionInsights
      .filter((x) => x.evidenceMode === "supporting")
      .slice(index * 4, index * 4 + 7)
      .map((x) => x.id),
    contradictoryEvidenceIds: interactionInsights
      .filter((x) => x.evidenceMode === "contradictory")
      .slice(0, index === 2 ? 1 : 2)
      .map((x) => x.id),
    owner: index === 0 ? "Maya Chen" : undefined,
    alertState:
      index === 0 ? "Investigating" : index === 2 ? "Resolved" : "New",
    deliveryState: [
      "Web · 09:44",
      index === 0 ? "Teams · delivered 09:45" : "Daily brief · queued",
    ],
    confidence: change.confidence,
    impact: change.impact,
  }),
);

export const demandDrivers: DemandDriver[] = [
  [
    "Payment date confusion",
    "Failure",
    5840,
    10.3,
    24,
    31,
    38,
    64,
    96400,
    "Current Account",
    "Plan change billing",
    "Payment query",
    "Date mismatch",
    "High",
  ],
  [
    "Order status",
    "Information",
    4920,
    8.7,
    -13,
    9,
    72,
    91,
    43100,
    "Delivery",
    "Order tracking",
    "Order status",
    "Tracking reassurance",
    "Medium",
  ],
  [
    "Identity verification failure",
    "Failure",
    4210,
    7.5,
    38,
    27,
    18,
    58,
    81600,
    "Mobile App",
    "Identity verification",
    "Account access",
    "Document capture failure",
    "High",
  ],
  [
    "Policy renewal explanation",
    "Reassurance",
    3760,
    6.7,
    17,
    22,
    51,
    76,
    68200,
    "Insurance",
    "Policy renewal",
    "Renewal query",
    "Price explanation",
    "Medium",
  ],
  [
    "Plan comparison",
    "Sales",
    3180,
    5.6,
    8,
    12,
    96,
    88,
    29700,
    "Current Account",
    "Product selection",
    "Sales enquiry",
    "Plan comparison",
    "Low",
  ],
  [
    "Refund progress",
    "Necessary service",
    2940,
    5.2,
    6,
    26,
    64,
    72,
    51900,
    "Current Account",
    "Refund",
    "Refund",
    "Refund progress",
    "Medium",
  ],
  [
    "Password recovery",
    "Avoidable",
    2680,
    4.7,
    -4,
    18,
    12,
    84,
    39400,
    "Mobile App",
    "Authentication",
    "Password",
    "Password recovery",
    "High",
  ],
  [
    "Fee complaint",
    "Complaint",
    2180,
    3.9,
    11,
    16,
    73,
    68,
    44800,
    "Current Account",
    "Fees",
    "Complaint",
    "Unexpected fee",
    "Medium",
  ],
  [
    "Cancellation request",
    "Retention",
    1760,
    3.1,
    5,
    14,
    120,
    81,
    37600,
    "Insurance",
    "Retention",
    "Cancellation",
    "Cancellation intent",
    "Low",
  ],
].map(
  (r, index) =>
    ({
      id: id(600 + index),
      name: r[0],
      class: r[1],
      volume: r[2],
      share: r[3],
      change: r[4],
      repeatRate: r[5],
      timeToRepeatHours: r[6],
      resolutionRate: r[7],
      serviceCost: r[8],
      product: r[9],
      process: r[10],
      statedReason: r[11],
      inferredReason: r[12],
      avoidability: r[13],
      confidence: confidence(
        84 + index,
        `${18 + index * 3} sampled interactions agree with operational metadata`,
        88 + index,
      ),
    }) as DemandDriver,
);

export const journeys: CustomerJourney[] = [
  {
    id: id(501),
    family: "Complete identity verification",
    cohort: "Android 14 · new customers",
    product: "Mobile App",
    steps: [
      { label: "Begin application", channel: "App", at: "08:12", friction: 1 },
      {
        label: "Document capture fails",
        channel: "App",
        at: "08:18",
        friction: 9,
      },
      { label: "Open web help", channel: "Chat", at: "08:26", friction: 7 },
      { label: "Call support", channel: "Phone", at: "09:04", friction: 8 },
    ],
    transfers: 1,
    switches: 2,
    repeatedExplanation: true,
    resolutionState: "Escalated",
    timeToRepeatHours: 18,
    effortSignals: ["3 attempts", "2 channel switches", "repeated explanation"],
    finalOutcome: "Manual identity review",
    sourceReferences: ["APP-81192", "CHT-39012", "CAS-24819"],
  },
  {
    id: id(502),
    family: "Confirm direct-debit date",
    cohort: "Mid-cycle plan changers",
    product: "Current Account",
    steps: [
      { label: "Plan changed", channel: "App", at: "Mon", friction: 2 },
      { label: "Email received", channel: "Email", at: "Tue", friction: 5 },
      { label: "Portal checked", channel: "App", at: "Tue", friction: 7 },
      { label: "Contact support", channel: "Chat", at: "Wed", friction: 6 },
    ],
    transfers: 0,
    switches: 2,
    repeatedExplanation: false,
    resolutionState: "Resolved",
    timeToRepeatHours: 72,
    effortSignals: ["conflicting dates", "channel switch"],
    finalOutcome: "Date confirmed",
    sourceReferences: ["APP-71320", "EML-81740", "CHT-36108"],
  },
  {
    id: id(503),
    family: "Track card delivery",
    cohort: "Digital only",
    product: "Delivery",
    steps: [
      {
        label: "Dispatch notification",
        channel: "Email",
        at: "09:00",
        friction: 1,
      },
      { label: "Open tracking", channel: "App", at: "09:08", friction: 1 },
      { label: "Delivery confirmed", channel: "App", at: "14:20", friction: 1 },
    ],
    transfers: 0,
    switches: 0,
    repeatedExplanation: false,
    resolutionState: "Resolved",
    effortSignals: ["self-service complete"],
    finalOutcome: "No assisted contact",
    sourceReferences: ["EML-82014", "APP-99214"],
  },
  {
    id: id(504),
    family: "Understand insurance renewal price",
    cohort: "Annual renewal · phone customers",
    product: "Insurance",
    steps: [
      {
        label: "Renewal letter received",
        channel: "Email",
        at: "Mon",
        friction: 3,
      },
      {
        label: "Compare previous premium",
        channel: "App",
        at: "Tue",
        friction: 6,
      },
      {
        label: "Call for price explanation",
        channel: "Phone",
        at: "Wed",
        friction: 8,
      },
    ],
    transfers: 0,
    switches: 2,
    repeatedExplanation: true,
    resolutionState: "Resolved",
    timeToRepeatHours: 51,
    effortSignals: ["price comparison", "channel switch", "long explanation"],
    finalOutcome: "Renewal price explained",
    sourceReferences: ["EML-82610", "APP-99821", "CAS-25104"],
  },
];

export const interventions: Intervention[] = [
  {
    id: id(701),
    type: "Measured intervention",
    title: "Revised delivery-tracking language",
    linkedInsightId: id(203),
    hypothesisId: id(303),
    owner: "Theo Grant",
    targetCohort: "Delivery customers",
    baseline: 5655,
    target: 5100,
    successMetric: "Order-status contacts",
    guardrails: [
      { label: "Failed delivery complaints", limit: 4.5, actual: 4.1 },
    ],
    launchDate: "8 Jul",
    observationWindow: "30 days",
    comparisonSeries: [5600, 5660, 5610, 5690, 5630, 5650],
    actualSeries: [5640, 5520, 5380, 5160, 5010, 4920],
    status: "Effective",
    measuredResult:
      "Order-status contacts fell 13%; 95% interval excludes the baseline.",
    unintendedEffects: "No material change in failed-delivery complaints.",
    externalReference: "ADO-4821",
  },
  {
    id: id(702),
    type: "Corrective action",
    title: "Rollback Android document-capture SDK",
    linkedInsightId: id(202),
    hypothesisId: id(301),
    owner: "Maya Chen",
    targetCohort: "Android 14 new customers",
    baseline: 16.7,
    target: 12.0,
    successMetric: "Verification abandonment rate",
    guardrails: [{ label: "Manual review rate", limit: 9, actual: 7.8 }],
    launchDate: "18 Jul",
    observationWindow: "14 days",
    comparisonSeries: [11.3, 11.6, 11.2, 11.8, 11.4, 11.5],
    actualSeries: [16.7, 16.1, 15.2, 14.4, 13.9, 13.4],
    status: "Measuring",
    measuredResult: "Early decline observed; five further days required.",
    unintendedEffects: "Manual review remains within guardrail.",
    externalReference: "ADO-4898",
  },
  {
    id: id(703),
    type: "Measured intervention",
    title: "Renewal price-breakdown test",
    linkedInsightId: id(204),
    hypothesisId: id(303),
    owner: "Priya Shah",
    targetCohort: "Insurance renewals",
    baseline: 12.4,
    target: 9.0,
    successMetric: "Average handling minutes",
    guardrails: [{ label: "Cancellation rate", limit: 7, actual: 6.9 }],
    launchDate: "4 Jul",
    observationWindow: "21 days",
    comparisonSeries: [12.1, 12.2, 12.0, 12.4, 12.3, 12.2],
    actualSeries: [12.4, 11.9, 11.5, 11.8, 11.4, 11.7],
    status: "Inconclusive",
    measuredResult:
      "Direction is favourable, but the interval overlaps no change.",
    unintendedEffects:
      "Cancellation is close to the guardrail; continue observation.",
    externalReference: "JIRA-CX-144",
  },
  {
    id: id(704),
    type: "Investigation",
    title: "Align portal and email collection-date logic",
    linkedInsightId: id(201),
    hypothesisId: id(302),
    owner: "Liam Walsh",
    targetCohort: "Mid-cycle plan changers",
    baseline: 31,
    target: 18,
    successMetric: "Repeat-contact rate",
    guardrails: [{ label: "Payment failures", limit: 2.1, actual: 1.8 }],
    launchDate: "22 Jul",
    observationWindow: "30 days",
    comparisonSeries: [29, 30, 31, 30],
    actualSeries: [31, 31, 30, 31],
    status: "Approved",
    measuredResult: "Not launched.",
    unintendedEffects: "None observed.",
    externalReference: "ADO-4912",
  },
];

export const connectorHealth: ConnectorHealth[] = [
  {
    id: id(801),
    source: "Dynamics 365",
    lastSuccessfulSync: "10:18",
    expected: 32240,
    received: 32198,
    eligible: 30410,
    processed: 30382,
    exclusions: 1788,
    failures: 28,
    lagMinutes: 6,
    reconciliationState: "Reconciled",
    nextRun: "10:33",
  },
  {
    id: id(802),
    source: "Genesys Cloud",
    lastSuccessfulSync: "10:12",
    expected: 18100,
    received: 17842,
    eligible: 17490,
    processed: 17392,
    exclusions: 352,
    failures: 98,
    lagMinutes: 14,
    reconciliationState: "Warning",
    nextRun: "10:27",
  },
  {
    id: id(803),
    source: "Northstar App",
    lastSuccessfulSync: "10:20",
    expected: 6140,
    received: 6140,
    eligible: 6032,
    processed: 6028,
    exclusions: 108,
    failures: 4,
    lagMinutes: 3,
    reconciliationState: "Reconciled",
    nextRun: "10:35",
  },
];

export const taxonomyVersions: TaxonomyVersion[] = [
  {
    id: id(901),
    version: "v12.4",
    state: "Published",
    changes: [
      "Split Account access > Identity verification",
      "Renamed Delivery status to Delivery tracking",
    ],
    impactedSavedAssets: [
      "Executive brief",
      "Digital operations alert",
      "Weekly service report",
    ],
    testSample: 1200,
    approval: "Amelia Hart",
    publishTime: "15 Jul, 16:30",
  },
  {
    id: id(902),
    version: "v12.5",
    state: "Review",
    changes: [
      "Promote document capture failure",
      "Merge collection date and payment schedule",
    ],
    impactedSavedAssets: [
      "Identity watch",
      "Billing repeat contact",
      "Product weekly brief",
    ],
    testSample: 800,
  },
];

export const analysisCoverage = {
  expected: 56480,
  received: 56180,
  eligible: 53932,
  processed: 53802,
  excluded: 2248,
  failed: 130,
};
export const weeklyVolume = [
  50238, 51110, 50780, 51920, 52310, 53140, 54520, 56480,
];
export const expectedLow = [
  49300, 49700, 49900, 50200, 50700, 51100, 51500, 51900,
];
export const expectedHigh = [
  52100, 52500, 52800, 53100, 53500, 53900, 54300, 54700,
];
export const periods = [
  "27 May",
  "3 Jun",
  "10 Jun",
  "17 Jun",
  "24 Jun",
  "1 Jul",
  "8 Jul",
  "15 Jul",
];

export const themeHierarchy = [
  {
    id: "payments",
    name: "Payments & billing",
    volume: 12840,
    share: 22.7,
    change: 18,
    novelty: 12,
    persistence: 92,
    effort: 6.8,
    outcome: 68,
    confidence: 92,
    children: [
      {
        name: "Direct debit",
        volume: 7240,
        children: ["Collection date mismatch", "Duplicate warning"],
      },
      {
        name: "Refunds",
        volume: 2940,
        children: ["Refund progress", "Missing payment date"],
      },
    ],
  },
  {
    id: "access",
    name: "Account access",
    volume: 9460,
    share: 16.8,
    change: 29,
    novelty: 38,
    persistence: 81,
    effort: 8.2,
    outcome: 61,
    confidence: 89,
    children: [
      {
        name: "Identity verification",
        volume: 4210,
        children: ["Document capture", "Device permissions"],
      },
      {
        name: "Password recovery",
        volume: 2680,
        children: ["Reset loop", "Device recognition"],
      },
    ],
  },
  {
    id: "delivery",
    name: "Orders & delivery",
    volume: 8120,
    share: 14.4,
    change: -9,
    novelty: 8,
    persistence: 74,
    effort: 3.4,
    outcome: 91,
    confidence: 94,
    children: [
      {
        name: "Delivery tracking",
        volume: 4920,
        children: ["Status clarity", "Missed delivery"],
      },
    ],
  },
  {
    id: "renewal",
    name: "Renewal & retention",
    volume: 6840,
    share: 12.1,
    change: 13,
    novelty: 16,
    persistence: 87,
    effort: 6.9,
    outcome: 76,
    confidence: 86,
    children: [
      {
        name: "Price explanation",
        volume: 3760,
        children: ["Renewal breakdown", "Policy fee"],
      },
      {
        name: "Cancellation",
        volume: 1760,
        children: ["Terms", "Competitor mention"],
      },
    ],
  },
] as const;

export const emergingLanguage = [
  {
    phrase: "camera keeps closing",
    count: 286,
    firstSeen: "2 Jul",
    acceleration: 61,
    proposal: "Account access > Identity verification > Document capture",
  },
  {
    phrase: "date moved again",
    count: 194,
    firstSeen: "9 Jul",
    acceleration: 34,
    proposal: "Payments > Direct debit > Collection date mismatch",
  },
  {
    phrase: "price without a reason",
    count: 118,
    firstSeen: "10 Jul",
    acceleration: 22,
    proposal: "Renewal > Price explanation > Renewal breakdown",
  },
];
